/**
 * PERSON FORM — Modale per creare o modificare un contatto.
 *
 * Thin wrapper: Dialog (stesse classi .app-dialog/.tc-head del "Crea task",
 * cosi' creazione contatto e creazione task si presentano allo stesso modo)
 * + PersonFormBody + logica thunk. I campi sono in PersonFormBody
 * (riutilizzato da AddRelationDrawer, che lo tiene a colonna singola nel
 * drawer); i bottoni Salva/Annulla vivono nel footer del Dialog e puntano al
 * form via attribute `form={formId}`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPerson, createPerson, updatePerson } from './personThunks';
import { PersonFormBody } from './PersonFormBody';
import type { PersonCreatePayload } from './personTypes';
import { addEmailAddress, updateEmailAddress } from '../emailAddress/emailAddressThunks';
import { addPhone, updatePhone } from '../phone/phoneThunks';
import { useToast } from '../../components/useToast';

interface PersonFormProps {
  onClose: () => void;
  onSaved?: () => void;
  /** Se presente, il drawer entra in modalita' edit (fetch + update). */
  personId?: number | null;
}

const FORM_ID = 'person-form';

export default function PersonForm({ onClose, onSaved, personId }: PersonFormProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = personId != null;
  const [submitting, setSubmitting] = useState(false);
  // Bloccato dal check di univocità email in PersonFormBody: submit disabilitato.
  const [blocked, setBlocked] = useState(false);
  const { currentItem } = useAppSelector((state) => state.persons);

  // Nessuna pulizia di `currentItem` allo smontaggio: quando questa modale si apre
  // DENTRO PersonDetail è lo stesso slot che la scheda sta mostrando, e azzerarlo
  // alla chiusura la lasciava appesa a "Caricamento...". Il rischio che la clear
  // copriva — prefill col contatto sbagliato rimasto in store — lo copre ora il
  // controllo sull'id in `initialValues`.
  useEffect(() => {
    if (isEdit && personId) dispatch(fetchPerson(personId));
  }, [isEdit, personId, dispatch]);

  // initialValues passati al body: in edit mode li prendiamo da currentItem
  // dopo il fetch; in create mode lasciamo che il body usi i suoi default.
  // I campi email/phone sono prefillati col contatto PRIMARIO esistente (find
  // primary, fallback al primo); al submit vengono aggiornati (vedi handleSubmit).
  // Memoizziamo sul reference di currentItem (stabile da Redux finche' l'item
  // non cambia): altrimenti un qualsiasi re-render del parent genererebbe un
  // nuovo oggetto, il body farebbe setForm(initialValues) nel useEffect e
  // sovrascriverebbe quanto l'utente sta digitando.
  const initialValues = useMemo<PersonCreatePayload | undefined>(() => {
    // L'id deve corrispondere: in store può esserci ancora un altro contatto.
    if (!isEdit || !currentItem || currentItem.personId !== personId) return undefined;
    const primaryEmail =
      currentItem.emailAddresses?.find((e) => e.primary) ?? currentItem.emailAddresses?.[0];
    const primaryPhone =
      currentItem.phones?.find((p) => p.primary) ?? currentItem.phones?.[0];
    return {
      externalCode: currentItem.externalCode ?? '',
      firstName: currentItem.firstName, lastName: currentItem.lastName,
      location: currentItem.location ?? '',
      personSourceId: currentItem.personSourceId,
      personSource: currentItem.personSource,
      personChannelId: currentItem.personChannelId,
      personChannel: currentItem.personChannel,
      leadStatus: currentItem.leadStatus, marketingStatus: currentItem.marketingStatus,
      email: primaryEmail?.address ?? '',
      phone: primaryPhone?.number ?? '',
    };
  }, [isEdit, currentItem, personId]);

  async function handleSubmit(payload: PersonCreatePayload) {
    setSubmitting(true);
    try {
      // 1. Create / update del Person. Errore qui e' BLOCCANTE: toast
      // d'errore, form resta aperta cosi' l'utente puo' correggere il payload
      // (es. validation backend fallita). In CREATE email e telefono viaggiano
      // nel POST e si salvano nella stessa transazione: finito qui.
      const { email, phone, ...personPayload } = payload;
      try {
        if (isEdit && personId) {
          await dispatch(updatePerson({ ...personPayload, personId })).unwrap();
        } else {
          await dispatch(createPerson(payload)).unwrap();
        }
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }

      // 2. Solo in EDIT: email/telefono PRIMARIO (best-effort), errori in
      // toast ma NON rollbacciano il Person (già aggiornato).
      //  - senza primario esistente: add del valore come primario.
      //  - con primario esistente: update di quel record se il valore è
      //    cambiato; campo vuoto = nessuna modifica (la rimozione si fa inline
      //    dal dettaglio, per non rendere distruttivo il drawer). La lista
      //    completa resta gestita da PersonDetail.
      const resultPersonId = isEdit && personId ? personId : 0;
      const trimmedEmail = resultPersonId ? email?.trim() ?? '' : '';
      const trimmedPhone = resultPersonId ? phone?.trim() ?? '' : '';

      const primaryEmail =
        currentItem?.emailAddresses?.find((e) => e.primary) ?? currentItem?.emailAddresses?.[0];
      const primaryPhone =
        currentItem?.phones?.find((p) => p.primary) ?? currentItem?.phones?.[0];

      if (trimmedEmail) {
        try {
          if (primaryEmail) {
            if (trimmedEmail !== primaryEmail.address) {
              await dispatch(updateEmailAddress({
                className: 'Person', classPK: resultPersonId,
                emailAddressId: primaryEmail.emailAddressId,
                address: trimmedEmail, primary: true,
              })).unwrap();
            }
          } else {
            await dispatch(addEmailAddress({
              className: 'Person', classPK: resultPersonId,
              address: trimmedEmail, primary: true,
            })).unwrap();
          }
        } catch (err) {
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
      if (trimmedPhone) {
        try {
          if (primaryPhone) {
            if (trimmedPhone !== primaryPhone.number) {
              await dispatch(updatePhone({
                className: 'Person', classPK: resultPersonId,
                phoneId: primaryPhone.phoneId,
                number: trimmedPhone, primary: true,
              })).unwrap();
            }
          } else {
            await dispatch(addPhone({
              className: 'Person', classPK: resultPersonId,
              number: trimmedPhone, primary: true,
            })).unwrap();
          }
        } catch (err) {
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }

      toast({ severity: 'success', message: t('common.saved') });
      onSaved?.();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
      <Button type="submit" form={FORM_ID} variant="contained" disabled={submitting || blocked}>
        {isEdit ? t('common.save') : t('common.create')}
      </Button>
    </>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      {/* Header chiaro con kicker, come il modale "Nuovo task": niente barra
          brand, il titolo e' solo un'etichetta di contesto. */}
      <DialogTitle className="tc-head">
        <span className="tc-kicker">{isEdit ? t('person.editContact') : t('person.newContact')}</span>
        <IconButton aria-label={t('common.cancel')} onClick={onClose} size="small" className="tc-x">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <PersonFormBody
          formId={FORM_ID}
          className="pf-grid"
          initialValues={initialValues}
          onSubmit={handleSubmit}
          currentPersonId={personId ?? undefined}
          onBlockingChange={setBlocked}
        />
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
